import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { BUTTON_VARIANTS } from 'ui/button';
import { Edit16 } from 'ui/icons';
import noop, { NoOp } from 'utils/noop';
import { addNegativeToast } from 'modules/toastContainer';

import { displayContent } from '../content';
import {
  CharacterCounter,
  StyledIconButton,
  StyledInput,
  StyledInputWrapper,
  TemplateNameDisplay,
} from '../EditableHeader/EditableHeader.styled';

export type Props = {
  isCompact?: boolean;
  isInverted?: boolean;
  isRequired?: boolean;
  maxLength?: number;
  onBlur?: NoOp;
  onChange: (str: string) => void;
  placeholder?: string; // required if isRequired === true (otherwise error messaging will be unclear)
  reset?: NoOp; // recommended if isRequired === true
  value: string;
  isDisabled?: boolean;
};

export const EditableInput = ({
  isCompact = false,
  isInverted = false,
  isRequired = false,
  isDisabled = false,
  maxLength,
  onBlur = noop,
  onChange,
  placeholder = '',
  reset = noop,
  value,
}: Props) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handleBlur = () => {
    if (!value.trim() && isRequired) {
      dispatch(addNegativeToast(displayContent.errFieldIsRequired({ fieldName: placeholder })));
      reset();
    } else {
      onBlur();
    }
    setIsEditMode(false);
  };

  return (
    <>
      {isEditMode ? (
        <StyledInputWrapper>
          <StyledInput
            autoFocus
            data-test="page-header-title-input"
            id="edit-template-name-input"
            isInverted={isInverted}
            onBlur={handleBlur}
            onChange={onChange}
            maxLength={maxLength}
            placeholder={placeholder}
            value={value}
            isRequired
          />
          {maxLength && (
            <CharacterCounter isCompact={isCompact} isInverted={isInverted}>
              {displayContent.strCharacterLengthDynamicMaxCount({
                maxCharCount: maxLength,
                charCount: value.length,
              })}
            </CharacterCounter>
          )}
        </StyledInputWrapper>
      ) : (
        <>
          <TemplateNameDisplay data-test="page-header-template-name" isInverted={isInverted}>
            {value}
          </TemplateNameDisplay>
          {!isDisabled && (
            <StyledIconButton
              data-test="page-header-edit-button"
              onClick={() => setIsEditMode(true)}
              variant={isInverted ? BUTTON_VARIANTS.GHOSTINVERTED : BUTTON_VARIANTS.GHOST}
            >
              <Edit16 />
            </StyledIconButton>
          )}
        </>
      )}
    </>
  );
};
